import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FiBook, FiCalendar, FiCheckCircle } from "react-icons/fi";
import { settingsAPI } from "../services/api";

const colorMap = {
  blue: {
    grad: "from-blue-500 to-blue-700",
    bg: "bg-blue-50",
    border: "border-blue-100",
    text: "text-blue-600",
    dot: "bg-blue-600",
  },
  primary: {
    grad: "from-primary-500 to-primary-700",
    bg: "bg-primary-50",
    border: "border-primary-100",
    text: "text-primary-600",
    dot: "bg-primary-600",
  },
  secondary: {
    grad: "from-secondary-500 to-secondary-700",
    bg: "bg-secondary-50",
    border: "border-secondary-100",
    text: "text-secondary-700",
    dot: "bg-secondary-700",
  },
};

const fallbackPrograms = [
  {
    _id: "1",
    title: "Montessori / Pre-Primary",
    grades: "Play Group - UKG",
    age: "Age: ~2.5 – 5 years",
    colorScheme: "blue",
    subjects: [
      "Play Group (PG)",
      "Nursery",
      "Lower Kindergarten (LKG)",
      "Upper Kindergarten (UKG)"
    ],
    description: "Our Pre-Primary program focuses on basic learning, play, and behavior to build a strong early foundation for young minds.",
    highlights: [
      "Play-based learning environment",
      "Focus on basic behavior and socialization",
      "Interactive games and storytelling",
      "Caring and trained Montessori teachers"
    ],
  },
  {
    _id: "2",
    title: "Primary Level",
    grades: "Class 1 - 5",
    age: "Age: ~6 – 10 years",
    colorScheme: "primary",
    subjects: [
      "Class 1",
      "Class 2",
      "Class 3",
      "Class 4",
      "Class 5",
      "Mathematics",
      "English",
      "Science"
    ],
    description: "Dedicated to foundational knowledge and mastery of basic core subjects like Math, English, and Science.",
    highlights: [
      "Strong foundation in basic subjects",
      "Regular assessments and progress tracking",
      "Focus on reading and writing skills",
      "Extracurricular activities integration"
    ],
  },
  {
    _id: "3",
    title: "Lower Secondary Level",
    grades: "Class 6 - 8",
    age: "Age: ~11 – 13 years",
    colorScheme: "secondary",
    subjects: [
      "Class 6",
      "Class 7",
      "Class 8",
      "Advanced Math",
      "General Science",
      "Social Studies"
    ],
    description: "Designed for concept building, critical thinking, and introduction to more advanced subjects bridging primary and high school.",
    highlights: [
      "Concept building methodologies",
      "Introduction to advanced subjects",
      "Critical thinking and problem solving",
      "Project-based learning"
    ],
  },
  {
    _id: "4",
    title: "Secondary Level",
    grades: "Class 9 - 10",
    age: "Age: ~14 – 16 years",
    colorScheme: "primary",
    subjects: [
      "Class 9",
      "Class 10",
      "Compulsory Math",
      "Optional Math",
      "Science",
      "English"
    ],
    description: "Rigorous academic preparation focusing on the SEE (School Leaving Certificate) under the National Examination Board.",
    highlights: [
      "SEE Board Exam preparation",
      "Comprehensive syllabus coverage",
      "Mock tests and revision classes",
      "Career counseling for higher education"
    ],
  },
];

const fallbackCalendar = [
  { month: "April", event: "New Academic Session Begins" },
  { month: "May", event: "First Unit Test" },
  { month: "July", event: "Mid-Term Examinations" },
  { month: "August", event: "Sports Day & Cultural Programs" },
  { month: "October", event: "Second Unit Test" },
  { month: "December", event: "Annual Examinations" },
  { month: "February", event: "Board Examinations (Grade 10 & 12)" },
  { month: "March", event: "Academic Year Ends / Results" },
];

const Academics = () => {
  const [programs, setPrograms] = useState(fallbackPrograms);
  const [calendarEvents, setCalendarEvents] = useState(fallbackCalendar);

  useEffect(() => {
    settingsAPI
      .get()
      .then((res) => {
        const s = res.data?.data?.settings;
        if (s?.programs?.length > 0) setPrograms(s.programs);
        if (s?.academicCalendar?.length > 0)
          setCalendarEvents(s.academicCalendar);
      })
      .catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <div className="hero-gradient py-24 text-center relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle, #bfdbfe 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <p className="text-secondary-400 text-sm font-semibold uppercase tracking-widest mb-3">
            Education
          </p>
          <h1 className="text-5xl font-serif font-bold text-white mb-4">
            Academics
          </h1>
          <p className="text-white/70 text-lg max-w-xl mx-auto">
            Comprehensive academic programs for every stage of your child's
            educational journey
          </p>
        </motion.div>
      </div>

      {/* Programs */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {programs.map((program, idx) => {
            const c = colorMap[program.colorScheme] || colorMap.primary;
            return (
              <motion.div
                key={program._id || idx}
                id={program._id || idx}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
                className={`${c.bg} border ${c.border} rounded-3xl overflow-hidden`}
              >
                <div className={`h-2 bg-gradient-to-r ${c.grad}`} />
                <div className="p-8 lg:p-12">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                    <div>
                      <div className="flex items-center gap-3 mb-4">
                        {program.grades && (
                          <span
                            className={`px-3 py-1 ${c.bg} ${c.text} border ${c.border} rounded-full text-xs font-semibold`}
                          >
                            {program.grades}
                          </span>
                        )}
                        {program.age && (
                          <span className="text-gray-500 text-xs">
                            {program.age}
                          </span>
                        )}
                      </div>
                      <h2 className="text-3xl font-serif font-bold text-primary-500 mb-4">
                        {program.title}
                      </h2>
                      <p className="text-gray-600 leading-relaxed mb-6">
                        {program.description}
                      </p>
                      {program.highlights?.length > 0 && (
                        <div className="space-y-2">
                          {program.highlights.map((h, hIdx) => (
                            <div key={hIdx} className="flex items-center gap-3">
                              <FiCheckCircle
                                className={`w-4 h-4 ${c.text} flex-shrink-0`}
                              />
                              <span className="text-gray-700 text-sm">{h}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {program.subjects?.length > 0 && (
                      <div>
                        <h3 className="font-bold text-primary-500 mb-4 flex items-center gap-2">
                          <FiBook className="w-4 h-4" /> Subjects Offered
                        </h3>
                        <div className="grid grid-cols-2 gap-2">
                          {program.subjects.map((subject, sIdx) => (
                            <div
                              key={sIdx}
                              className="flex items-center gap-2 bg-white border border-gray-100 rounded-xl px-3 py-2 text-sm text-gray-700"
                            >
                              <div
                                className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.dot}`}
                              />
                              {subject}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Academic Calendar */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="section-subtitle mb-2">Planning Ahead</p>
            <h2 className="section-title mb-4">Academic Calendar</h2>
            <p className="text-gray-500">
              Key dates and events for the academic year 2025-2026
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {calendarEvents.map((event, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: idx % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                viewport={{ once: true }}
                className="flex items-center gap-4 bg-white border border-gray-100 rounded-xl p-4"
              >
                <div className="w-16 h-16 bg-primary-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <div className="text-center">
                    <FiCalendar className="w-4 h-4 text-secondary-400 mx-auto mb-0.5" />
                    <p className="text-white text-xs font-bold">
                      {(event.month || "").slice(0, 3)}
                    </p>
                  </div>
                </div>
                <p className="text-gray-700 font-medium text-sm">
                  {event.event}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Academics;
